import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Play, Square, Loader2, AlertCircle, CheckCircle2, Pencil, Trash2,
  CalendarPlus, Upload, Clock, RadioTower, XCircle, FileSpreadsheet, ExternalLink, AlertTriangle,
  Lock, Link2, RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  eventsApi, schedulesApi, uploadToS3, queryKeys,
  ApiError, type EventSummary, type StreamSchedule, type AgentCommandStatus, type EventFormat,
  type EventImportPlan, type ImportField,
} from '@/lib/api';
import { fmtInTz, toZonedInputValue, zonedInputToInstant } from '@/lib/utils';
import { useAuth } from '@/store/auth';
import ScheduleFormModal from '@/components/schedules/ScheduleFormModal';

const INPUT = 'w-full rounded border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';

// Thumbnail specs (YouTube-aligned): JPG/PNG, 16:9, ≥1280×720, under 2 MB.
const THUMB_MAX_BYTES = 2 * 1024 * 1024;
const THUMB_MIN_W = 1280;
const THUMB_MIN_H = 720;
const THUMB_SPEC = 'JPG or PNG · 16:9 · 1280×720 or larger · under 2 MB';

// Estimated game length (minutes) by format + ends — normal game, extra-end
// allowance disregarded (stops are manual). Used ONLY to flag tight/overlapping
// windows on the same sheet; never drives any scheduling action.
const GAME_MINUTES: Record<EventFormat, Record<number, number>> = {
  mixed_doubles: { 6: 65, 8: 85, 10: 105 },
  four_person: { 6: 100, 8: 130, 10: 160 },
};
const TIGHT_BUFFER_MIN = 15; // a next game starting within this of the estimated end = "tight"

const FORMAT_LABEL: Record<EventFormat, string> = { four_person: 'Four Person', mixed_doubles: 'Mixed Doubles' };

// Friendly labels for the per-stream startup checklist (from command stageHistory).
const STEP_LABEL: Record<string, string> = {
  queued: 'Queued', validating: 'Validated', youtube: 'Broadcast created',
  obs: 'OBS started', starting: 'Going on air', waiting_live: 'Waiting for live signal', live: 'Live on YouTube',
  discord: 'Confirmation sent to Discord',
};

function timeOnly(iso: string | null | undefined, tz?: string): string {
  if (!iso) return '';
  try { return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit', ...(tz ? { timeZone: tz } : {}) }); }
  catch { return ''; }
}

// Retained per-stream checklist: start milestones + stop. Prefers the live command's
// stageHistory (during startup); falls back to the match's persisted startupLog
// (retained after the command ages out / once live).
function StreamChecklist({ startCmd, match, tz }: { startCmd: AgentCommandStatus | undefined; match: StreamSchedule; tz?: string }) {
  let history: Array<{ stage: string; at: string }> = startCmd?.stageHistory ?? [];
  if (history.length === 0 && match.startupLog) {
    try { history = JSON.parse(match.startupLog) ?? []; } catch { history = []; }
  }
  const steps: Array<{ label: string; at?: string | null; ok: boolean }> = [];
  for (const h of history) steps.push({ label: STEP_LABEL[h.stage] ?? h.stage, at: h.at, ok: true });
  if (startCmd?.status === 'failed') steps.push({ label: startCmd.message || 'Start failed', at: startCmd.completedAt, ok: false });
  if (match.streamEndedAt) steps.push({ label: 'Stopped', at: match.streamEndedAt, ok: true });
  if (steps.length === 0) {
    return <p className="px-3 py-2 text-xs text-gray-400">No activity yet — steps will appear here as the stream starts.</p>;
  }
  return (
    <ul className="space-y-1 px-3 py-2">
      {steps.map((s, i) => (
        <li key={i} className="flex items-center gap-2 text-xs">
          {s.ok
            ? <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-green-600 dark:text-green-400" />
            : <AlertCircle className="h-3.5 w-3.5 shrink-0 text-red-600 dark:text-red-400" />}
          <span className={s.ok ? 'text-gray-700 dark:text-gray-300' : 'text-red-600 dark:text-red-400'}>{s.label}</span>
          {s.at && <span className="ml-auto tabular-nums text-gray-400">{timeOnly(s.at, tz)}</span>}
        </li>
      ))}
    </ul>
  );
}

function readImageDims(file: File): Promise<{ w: number; h: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { resolve({ w: img.naturalWidth, h: img.naturalHeight }); URL.revokeObjectURL(url); };
    img.onerror = () => { reject(new Error('Not a readable image')); URL.revokeObjectURL(url); };
    img.src = url;
  });
}

// Check an event thumbnail against the spec and upload it. Throws a readable message.
async function uploadEventThumbnail(facilityId: string, file: File): Promise<{ key: string; mime: string }> {
  if (!['image/jpeg', 'image/png'].includes(file.type)) throw new Error('Thumbnail must be a JPG or PNG.');
  if (file.size > THUMB_MAX_BYTES) throw new Error(`Thumbnail is ${(file.size / 1048576).toFixed(1)} MB — must be under 2 MB.`);
  const { w, h } = await readImageDims(file);
  if (w < THUMB_MIN_W || h < THUMB_MIN_H) throw new Error(`Image is ${w}×${h}. Minimum ${THUMB_MIN_W}×${THUMB_MIN_H} (16:9) required.`);
  const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
  const presign = await eventsApi.thumbnailPresign({ facilityId, contentType: file.type, ext, size: file.size });
  await uploadToS3({ url: presign.uploadUrl, fields: presign.fields }, file);
  return { key: presign.key, mime: file.type };
}

// Stages the agent reports while a start command runs.
const STAGE_ORDER = ['queued', 'validating', 'youtube', 'obs', 'starting', 'waiting_live', 'live'] as const;
const STAGE_LABEL: Record<string, string> = {
  queued: 'Queued', validating: 'Validating', youtube: 'Creating broadcast',
  obs: 'Starting OBS', starting: 'Going on air', waiting_live: 'Waiting for live signal', live: 'Live',
  streaming: 'Live',
};

function countdown(untilIso: string): string {
  const ms = new Date(untilIso).getTime() - Date.now();
  if (ms <= 0) return 'expired';
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  if (h >= 24) { const d = Math.floor(h / 24); return `${d}d ${h % 24}h`; }
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

// ── Per-match progress / live indicator ──────────────────────────────────────
function MatchProgress({ match, cmd }: { match: StreamSchedule; cmd: AgentCommandStatus | undefined }) {
  // A running/pending command takes precedence — show live staged progress.
  if (cmd && (cmd.status === 'pending' || cmd.status === 'delivered' || cmd.status === 'running')) {
    const idx = cmd.stage ? STAGE_ORDER.indexOf(cmd.stage as typeof STAGE_ORDER[number]) : -1;
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
        <Loader2 className="h-3 w-3 animate-spin" />
        {STAGE_LABEL[cmd.stage ?? 'queued'] ?? 'Starting'}
        {idx >= 0 && <span className="text-blue-400">({idx + 1}/{STAGE_ORDER.length})</span>}
      </span>
    );
  }
  if (cmd && cmd.status === 'failed') {
    return (
      <span title={cmd.message ?? 'Start failed'} className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900/40 dark:text-red-300">
        <AlertCircle className="h-3 w-3" /> Failed
      </span>
    );
  }
  // A start that finished ('done' = the agent confirmed the broadcast live) is
  // Live, even if the match row hasn't caught up: commands poll every 3 s but
  // match status every 15 s, and the gap read as "Not started" before "Live"
  // (Clint, Frogtown, 2026-10-24).
  if (match.status === 'streaming' || (match.status === 'pending' && cmd?.type === 'start' && cmd.status === 'done')) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/40 dark:text-green-300">
        <span className="relative flex h-1.5 w-1.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-green-500" />
        </span>
        Live
      </span>
    );
  }
  if (match.status === 'completed') {
    return <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-400"><CheckCircle2 className="h-3 w-3" /> Done</span>;
  }
  return <span className="text-xs text-gray-400">Not started</span>;
}

// ── CurlOps link (clubs whose schedule comes from CurlOps) ───────────────────
// A linked event takes its games from that CurlOps event, and CurlOps' Start game /
// Finished buttons start and stop the streams. From CSM, starts and stops then
// need the event's override code (admins confirm instead).
function CurlOpsLinkFields({ linked, onLinked, coId, onCoId, code, onCode, hasCode }: {
  linked: boolean; onLinked: (v: boolean) => void; coId: string; onCoId: (v: string) => void;
  code: string; onCode: (v: string) => void; hasCode?: boolean;
}) {
  return (
    <div className="space-y-2 rounded-md border border-teal-200 bg-teal-50 p-3 dark:border-teal-800 dark:bg-teal-900/20">
      <label className="flex items-center gap-2 text-sm font-medium text-teal-900 dark:text-teal-100">
        <input type="checkbox" className="h-4 w-4" checked={linked} onChange={e => onLinked(e.target.checked)} />
        CurlOps event
      </label>
      <p className="text-xs text-teal-800 dark:text-teal-200">
        Games come from CurlOps and stay in step with it. Streams start when <strong>Start game</strong> is pressed in
        CurlOps and stop at <strong>Finished</strong>. Starting or stopping here needs the override code.
      </p>
      {linked && (
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">CurlOps event ID</label>
            <input className={INPUT} inputMode="numeric" value={coId} onChange={e => onCoId(e.target.value.replace(/[^0-9]/g, ''))} placeholder="e.g. 5" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
              Override code {hasCode ? '(set — type a new one to replace it)' : '(at least 4 characters)'}
            </label>
            <input className={INPUT} type="password" autoComplete="new-password" value={code} onChange={e => onCode(e.target.value)} placeholder={hasCode ? '••••••' : 'For starting/stopping from CSM'} />
          </div>
        </div>
      )}
    </div>
  );
}

// What a start/stop of a CurlOps-linked game from CSM must send, after asking.
// {} = not linked (nothing extra); null = the user cancelled.
function curlOpsOverride(event: EventSummary, isAdmin: boolean, action: string): { override?: boolean; overrideCode?: string } | null {
  if (event.curlOpsEventId == null) return {};
  if (isAdmin) {
    return confirm(`"${event.name}" is run from CurlOps. ${action} from CSM anyway? This override is logged.`) ? { override: true } : null;
  }
  if (!event.hasOverrideCode) {
    alert(`"${event.name}" is run from CurlOps: use Start game / Finished there. No override code is set for this event, so only a CSM admin can override.`);
    return null;
  }
  const code = prompt(`"${event.name}" is run from CurlOps. To ${action.toLowerCase()} from CSM, enter the event's override code:`);
  return code ? { overrideCode: code } : null;
}

const CANCELLED = 'cancelled';
const lockAlert = (e: unknown) => {
  if (e instanceof Error && e.message === CANCELLED) return;
  if (e instanceof ApiError && e.status === 423) alert(e.message);
};

// Edit an event's CurlOps link: ID, override code, or unlink.
function CurlOpsSettings({ event, onDone }: { event: EventSummary; onDone: () => void }) {
  const qc = useQueryClient();
  const [linked, setLinked] = useState(event.curlOpsEventId != null);
  const [coId, setCoId] = useState(event.curlOpsEventId != null ? String(event.curlOpsEventId) : '');
  const [code, setCode] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => eventsApi.update(event.id, {
      curlOpsEventId: linked ? Number(coId) : null,
      ...(linked && code.trim() ? { overrideCode: code.trim() } : {}),
    }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: queryKeys.events.all });
      if (r.syncError) alert(`Saved, but the first sync from CurlOps failed: ${r.syncError}`);
      onDone();
    },
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : 'Could not save'),
  });
  const invalid = linked && (!coId || (!event.hasOverrideCode && code.trim().length < 4) || (!!code.trim() && code.trim().length < 4));
  return (
    <div className="space-y-2 rounded-lg border border-teal-300 bg-white p-3 dark:border-teal-800 dark:bg-gray-900">
      <CurlOpsLinkFields linked={linked} onLinked={setLinked} coId={coId} onCoId={setCoId} code={code} onCode={setCode} {...(event.hasOverrideCode ? { hasCode: true } : {})} />
      {!linked && event.curlOpsEventId != null && (
        <p className="text-xs text-amber-700 dark:text-amber-300">Unlinking removes the games still waiting from this event; they go back to the club's ordinary schedule and start on their own.</p>
      )}
      {err && <p className="text-sm text-red-600 dark:text-red-400">{err}</p>}
      {invalid && (
        <p className="text-right text-xs text-amber-700 dark:text-amber-300">
          Still needed: {!coId ? 'the CurlOps event ID' : 'an override code (4+ characters)'}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onDone}>Cancel</Button>
        <Button size="sm" onClick={() => { setErr(null); save.mutate(); }} disabled={invalid || save.isPending}>
          {save.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Link2 className="mr-1 h-4 w-4" />} Save
        </Button>
      </div>
    </div>
  );
}

// ── Edit an existing event's details (draft or in Event Mode) ────────────────
// The end time has its own controls (Start event / Extend) and the CurlOps link
// its own panel; this covers the rest of what the create form sets.
function EditEventDetails({ event, facilityId, onDone }: { event: EventSummary; facilityId: string; onDone: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState(event.name);
  const [format, setFormat] = useState<EventFormat>(event.format ?? 'four_person');
  const [ends, setEnds] = useState<number>(event.numEnds ?? 8);
  const [thumb, setThumb] = useState<{ key: string | null; mime: string | null }>({ key: event.thumbnailS3Key, mime: event.thumbnailMime });
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const save = useMutation({
    mutationFn: () => eventsApi.update(event.id, {
      name: name.trim(),
      format,
      numEnds: ends,
      ...(thumb.key !== event.thumbnailS3Key ? { thumbnailS3Key: thumb.key, thumbnailMime: thumb.mime } : {}),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: queryKeys.events.all }); onDone(); },
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : 'Could not save'),
  });

  const onPick = async (file: File) => {
    setErr(null); setUploading(true);
    try { const t = await uploadEventThumbnail(facilityId, file); setThumb({ key: t.key, mime: t.mime }); }
    catch (e) { setErr(e instanceof Error ? e.message : 'Thumbnail upload failed'); }
    finally { setUploading(false); }
  };

  return (
    <div className="space-y-3 rounded-lg border border-gray-300 bg-white p-3 dark:border-gray-700 dark:bg-gray-900">
      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Edit event details</p>
      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Event name</label>
        <input className={INPUT} value={name} onChange={e => setName(e.target.value)} />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Format</label>
          <select className={INPUT} value={format} onChange={e => setFormat(e.target.value as EventFormat)}>
            <option value="four_person">Four Person</option>
            <option value="mixed_doubles">Mixed Doubles</option>
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Number of Ends</label>
          <select className={INPUT} value={ends} onChange={e => setEnds(Number(e.target.value))}>
            <option value={6}>6 ends</option>
            <option value={8}>8 ends</option>
            <option value={10}>10 ends</option>
          </select>
        </div>
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Event thumbnail</label>
        <div className="flex flex-wrap items-center gap-2">
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) void onPick(f); }} />
          <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading}>
            {uploading ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Upload className="mr-1 h-4 w-4" />}
            {thumb.key ? 'Replace thumbnail' : 'Upload thumbnail'}
          </Button>
          {thumb.key && (
            <Button type="button" variant="outline" size="sm" onClick={() => setThumb({ key: null, mime: null })}>Remove</Button>
          )}
          {thumb.key && thumb.key !== event.thumbnailS3Key && (
            <span className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400"><CheckCircle2 className="h-3.5 w-3.5" /> uploaded</span>
          )}
        </div>
        <p className="mt-1 text-xs text-gray-400">{THUMB_SPEC}. Applies to games that start from now on; streams already live keep theirs.</p>
      </div>
      {err && <p className="text-sm text-red-600 dark:text-red-400">{err}</p>}
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onDone}>Cancel</Button>
        <Button size="sm" onClick={() => { setErr(null); save.mutate(); }} disabled={!name.trim() || uploading || save.isPending}>
          {save.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Pencil className="mr-1 h-4 w-4" />} Save
        </Button>
      </div>
    </div>
  );
}

// ── Create / activate event form ─────────────────────────────────────────────
function CreateEventForm({ facilityId, facilityTimezone, sheetsEnabled, curlOps, onCreated }: { facilityId: string; facilityTimezone?: string; sheetsEnabled: boolean; curlOps?: boolean; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [until, setUntil] = useState('');
  const [format, setFormat] = useState<EventFormat>('four_person');
  const [ends, setEnds] = useState<number>(8);
  const [sheetUrl, setSheetUrl] = useState('');
  const [thumbKey, setThumbKey] = useState<string | null>(null);
  const [thumbMime, setThumbMime] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [coLinked, setCoLinked] = useState(false);
  const [coId, setCoId] = useState('');
  const [coCode, setCoCode] = useState('');
  const linked = !!curlOps && coLinked;

  const create = useMutation({
    mutationFn: (status: 'draft' | 'active') => eventsApi.create({
      facilityId,
      name: name.trim(),
      status,
      ...(untilInstant ? { eventModeUntil: untilInstant.toISOString() } : {}),
      format,
      numEnds: ends,
      googleSheetUrl: linked ? null : (sheetUrl.trim() || null),
      thumbnailS3Key: thumbKey,
      thumbnailMime: thumbMime,
      ...(linked ? { curlOpsEventId: Number(coId), overrideCode: coCode.trim() } : {}),
    }),
    onSuccess: (r) => {
      if (r.syncError) alert(`Event saved, but the first sync from CurlOps failed: ${r.syncError}. Use Sync now on the event.`);
      onCreated();
    },
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : 'Failed to create event'),
  });

  const makeSheet = useMutation({
    mutationFn: () => eventsApi.createSheet({ facilityId, ...(name.trim() ? { name: name.trim() } : {}) }),
    onSuccess: (r) => { setSheetUrl(r.url); },
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : 'Sheet creation failed'),
  });

  const onPickThumb = async (file: File) => {
    setErr(null);
    setUploading(true);
    try {
      const t = await uploadEventThumbnail(facilityId, file);
      setThumbKey(t.key); setThumbMime(t.mime);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Thumbnail upload failed');
    } finally { setUploading(false); }
  };

  const hasName = !!name.trim();
  // The end-time box is rink time — an event ends when it ends at the facility,
  // not on the clock of whoever happens to be filling in the form.
  const untilInstant = zonedInputToInstant(until, facilityTimezone);
  const untilValid = !!untilInstant && untilInstant.getTime() > Date.now();
  const linkOk = !linked || (!!coId && coCode.trim().length >= 4);
  const canStartNow = hasName && untilValid && linkOk;
  // Say why the buttons are greyed out rather than leaving people to guess.
  const missing = [
    ...(!hasName ? ['an event name'] : []),
    ...(linked && !coId ? ['the CurlOps event ID'] : []),
    ...(linked && coCode.trim().length < 4 ? ['an override code (4+ characters)'] : []),
    ...(!untilValid ? ['a future "Ends at" time (only to start Event Mode now)'] : []),
  ];

  return (
    <Card>
      <CardHeader><CardTitle className="flex items-center gap-2"><CalendarPlus className="h-5 w-5" /> Set up an Event</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          <strong>Prepare a draft</strong> to set up an event in advance — import and edit matches while your
          league keeps auto-starting. When you're ready, <strong>Start event mode</strong> to pause the league
          and run the draws. Each match's title + thumbnail apply automatically as its stream goes live.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Event name</label>
            <input className={INPUT} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. 2026 Provincials" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Ends at (auto-expires)</label>
            <input type="datetime-local" className={INPUT} value={until} onChange={e => setUntil(e.target.value)} />
            <p className="mt-1 text-xs text-gray-400">Required to start now; optional for a draft (set it when you activate).</p>
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Format</label>
            <select className={INPUT} value={format} onChange={e => setFormat(e.target.value as EventFormat)}>
              <option value="four_person">Four Person</option>
              <option value="mixed_doubles">Mixed Doubles</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Number of Ends</label>
            <select className={INPUT} value={ends} onChange={e => setEnds(Number(e.target.value))}>
              <option value={6}>6 ends</option>
              <option value={8}>8 ends</option>
              <option value={10}>10 ends</option>
            </select>
          </div>
        </div>
        <p className="text-xs text-gray-400">
          Estimated game length: <strong>{GAME_MINUTES[format][ends]} min</strong> — used only to flag tight/overlapping windows on a sheet (advisory).
        </p>
        {curlOps && (
          <CurlOpsLinkFields linked={coLinked} onLinked={setCoLinked} coId={coId} onCoId={setCoId} code={coCode} onCode={setCoCode} />
        )}
        {!linked && <div>
          <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Shared Google Sheet URL (matches — optional)</label>
          <input className={INPUT} value={sheetUrl} onChange={e => setSheetUrl(e.target.value)} placeholder="https://docs.google.com/spreadsheets/…" />
          {sheetsEnabled && (
            <div className="mt-1.5 flex items-center gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => makeSheet.mutate()} disabled={makeSheet.isPending}>
                {makeSheet.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-1 h-4 w-4" />}
                Create Google Sheet
              </Button>
              {sheetUrl && (
                <a href={sheetUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline dark:text-blue-400">
                  <ExternalLink className="h-3.5 w-3.5" /> Open sheet
                </a>
              )}
              <span className="text-xs text-gray-400">creates a pre-formatted sheet in the shared Drive</span>
            </div>
          )}
        </div>}
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Event thumbnail (optional)</label>
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) void onPickThumb(f); }} />
            <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading}>
              {uploading ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Upload className="mr-1 h-4 w-4" />}
              {thumbKey ? 'Replace thumbnail' : 'Upload thumbnail'}
            </Button>
            {thumbKey && <span className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400"><CheckCircle2 className="h-3.5 w-3.5" /> uploaded</span>}
          </div>
          <p className="mt-1 text-xs text-gray-400">{THUMB_SPEC}</p>
        </div>
        {err && <p className="text-sm text-red-600 dark:text-red-400">{err}</p>}
        {missing.length > 0 && (
          <p className="text-right text-xs text-amber-700 dark:text-amber-300">Still needed: {missing.join(' · ')}</p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => create.mutate('draft')} disabled={!hasName || !linkOk || create.isPending} title="Save as a draft — the league keeps running; start it later">
            {create.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <CalendarPlus className="mr-1 h-4 w-4" />}
            Prepare event (draft)
          </Button>
          <Button onClick={() => create.mutate('active')} disabled={!canStartNow || create.isPending} title={canStartNow ? undefined : 'Enter a name and a future end time to start now'}>
            {create.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <RadioTower className="mr-1 h-4 w-4" />}
            Start event mode now
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Modal shell ──────────────────────────────────────────────────────────────
function ModalShell({ title, children, onClose }: { title: string; children: ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="max-h-[85vh] w-full max-w-2xl overflow-hidden rounded-lg bg-white shadow-xl dark:bg-gray-900" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-700">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
          <button onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"><XCircle className="h-5 w-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── Pre-flight: tick each game before a Draw Batch goes live ──────────────────
function PreflightModal({ drawStart, batch, facilityTimezone, stateBySheet, starting, onClose, onConfirm }: {
  drawStart: string;
  batch: StreamSchedule[];
  facilityTimezone?: string;
  stateBySheet: Map<string, { online: boolean; state: string }>;
  starting: boolean;
  onClose: () => void;
  onConfirm: (scheduleIds: number[]) => void;
}) {
  // Ticks choose which games start (Clint, Frogtown, 2026-10-24: the old
  // dialog said "all checked streams start" but needed every box ticked and
  // then started the whole draw). Paused games are listed but can't be ticked:
  // the server never starts a paused match.
  const pending = batch.filter(m => m.status === 'pending');
  const startable = pending.filter(m => !m.isPaused);
  const paused = pending.filter(m => m.isPaused);
  const [checked, setChecked] = useState<Record<number, boolean>>({});
  useEffect(() => { setChecked({}); }, [drawStart]);
  const selected = startable.filter(m => checked[m.id]);
  const allChecked = startable.length > 0 && selected.length === startable.length;
  const someChecked = selected.length > 0 && !allChecked;
  const toggleAll = () => setChecked(allChecked ? {} : Object.fromEntries(startable.map(m => [m.id, true])));

  return (
    <ModalShell title={`Pre-flight — Draw Batch ${fmtInTz(drawStart, facilityTimezone)}`} onClose={onClose}>
      <div className="max-h-[60vh] overflow-y-auto px-4 py-3">
        <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
          Tick the games to start. Ticked streams start together (staggered a few seconds apart). Title + event thumbnail are applied automatically as each stream goes live.
        </p>
        <p className="mb-3 flex items-start gap-1.5 rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>Event streams don't start or stop on their own. You start them here, and must <strong>Stop</strong> each one when its game ends.</span>
        </p>
        {startable.length > 1 && (
          <label className="mb-1.5 flex cursor-pointer items-center gap-3 px-3 py-1 text-sm font-medium text-gray-700 dark:text-gray-300">
            <input
              type="checkbox" className="h-4 w-4" checked={allChecked}
              ref={el => { if (el) el.indeterminate = someChecked; }}
              onChange={toggleAll}
            />
            Select all
          </label>
        )}
        <div className="space-y-1.5">
          {startable.map(m => {
            const st = stateBySheet.get(m.sheetId);
            return (
              <label key={m.id} className="flex cursor-pointer items-center gap-3 rounded border border-gray-200 px-3 py-2 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800/50">
                <input type="checkbox" className="h-4 w-4" checked={!!checked[m.id]} onChange={e => setChecked(c => ({ ...c, [m.id]: e.target.checked }))} />
                <span className="rounded bg-blue-100 px-1.5 py-0.5 text-xs font-semibold text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">Sheet {m.sheetId}</span>
                <span className="min-w-0 flex-1 truncate text-sm text-gray-800 dark:text-gray-200">
                  <span className="font-medium">{m.team1Name && m.team2Name ? `${m.team1Name} vs ${m.team2Name}` : (m.team1Name || m.team2Name || '—')}</span>
                  <span className="ml-2 text-gray-400">{m.eventName}</span>
                </span>
                {st ? (
                  st.online
                    ? <span className="text-xs text-green-600 dark:text-green-400">host online</span>
                    : <span className="text-xs font-medium text-red-600 dark:text-red-400">host OFFLINE</span>
                ) : <span className="text-xs text-gray-400">host unknown</span>}
              </label>
            );
          })}
          {paused.map(m => (
            <div key={m.id} className="flex items-center gap-3 rounded border border-dashed border-gray-200 px-3 py-2 text-gray-400 dark:border-gray-700 dark:text-gray-500">
              <input type="checkbox" className="h-4 w-4" disabled />
              <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-semibold text-gray-500 dark:bg-gray-800 dark:text-gray-400">Sheet {m.sheetId}</span>
              <span className="min-w-0 flex-1 truncate text-sm">{m.team1Name && m.team2Name ? `${m.team1Name} vs ${m.team2Name}` : (m.team1Name || m.team2Name || '—')}</span>
              <span className="text-xs">paused — won't start</span>
            </div>
          ))}
          {startable.length === 0 && <p className="text-sm text-gray-500 dark:text-gray-400">No not-yet-started games in this batch.</p>}
        </div>
      </div>
      <div className="flex items-center justify-between gap-2 border-t border-gray-200 px-4 py-3 dark:border-gray-700">
        <span className="text-xs text-gray-400">{selected.length} of {startable.length} selected</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={() => onConfirm(selected.map(m => m.id))} disabled={selected.length === 0 || starting}>
            {starting ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Play className="mr-1 h-4 w-4" />}
            {allChecked
              ? `Start all ${startable.length} stream${startable.length === 1 ? '' : 's'}`
              : `Start ${selected.length} of ${startable.length} streams`}
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}

// ── Import preview (grouped by draw) ─────────────────────────────────────────
// What a re-import would do, from the backend's plan (lib/eventImportPlan.ts):
// new / changed / removed / kept / problems / unchanged. Apply sends the plan's
// hash back; if the sheet changed meanwhile the server answers 409 with the new
// plan, which replaces this one in place.
const FIELD_LABEL: Record<ImportField, string> = { start: 'Time', sheet: 'Sheet', event: 'Event', team1: 'Team 1', team2: 'Team 2', notes: 'Notes' };
const teamsOf = (r: { team1Name: string | null; team2Name: string | null; eventName?: string }) =>
  r.team1Name && r.team2Name ? `${r.team1Name} vs ${r.team2Name}` : (r.team1Name || r.team2Name || r.eventName || '—');

// Pieces of the import review. Module-level so a re-render (ticking a box)
// doesn't remount them and snap open sections shut.
function ImportLine({ when, sheet, children }: { when: string; sheet: string; children: ReactNode }) {
  return (
    <div className="flex items-start gap-2 py-1.5 text-sm">
      <span className="w-40 shrink-0 text-xs text-gray-500 dark:text-gray-400">{when}</span>
      <span className="shrink-0 rounded bg-blue-100 px-1.5 py-0.5 text-xs font-semibold text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">Sheet {sheet}</span>
      <span className="min-w-0 flex-1 text-gray-800 dark:text-gray-200">{children}</span>
    </div>
  );
}
function ImportSection({ title, count, tone, open, children }: { title: string; count: number; tone: string; open?: boolean; children: ReactNode }) {
  if (count === 0) return null;
  return (
    <details open={open} className="mb-2 rounded border border-gray-200 dark:border-gray-700">
      <summary className={`cursor-pointer select-none px-3 py-2 text-xs font-semibold uppercase ${tone}`}>{title} ({count})</summary>
      <div className="divide-y divide-gray-100 border-t border-gray-100 px-3 dark:divide-gray-800 dark:border-gray-800">{children}</div>
    </details>
  );
}
function AckTick({ checked, onChange, tone, children }: { checked: boolean; onChange: (v: boolean) => void; tone: 'amber' | 'red'; children: ReactNode }) {
  return (
    <label className={`mb-2 flex cursor-pointer items-start gap-2 rounded border px-2 py-2 text-xs ${tone === 'red'
      ? 'border-red-300 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200'
      : 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200'}`}>
      <input type="checkbox" className="mt-0.5 h-3.5 w-3.5" checked={checked} onChange={e => onChange(e.target.checked)} />
      <span><AlertTriangle className="mb-0.5 mr-1 inline h-3.5 w-3.5" />{children}</span>
    </label>
  );
}

function ImportPreviewModal({ eventId, facilityTimezone, onClose, onImported }: {
  eventId: number;
  facilityTimezone?: string;
  onClose: () => void;
  onImported: () => void;
}) {
  const preview = useQuery({
    queryKey: ['events', 'import-preview', eventId],
    queryFn: () => eventsApi.importMatches(eventId, { confirm: false }),
    refetchOnWindowFocus: false,
  });
  // A 409 on Apply hands back a newer plan; show that one instead.
  const [newer, setNewer] = useState<EventImportPlan | null>(null);
  const plan = newer ?? preview.data?.plan ?? null;
  const [acks, setAcks] = useState({ underway: false, remove: false, dragFill: false });
  const doImport = useMutation({
    mutationFn: () => eventsApi.importMatches(eventId, { confirm: true, planHash: plan!.hash, acks }),
    onSuccess: () => { onImported(); onClose(); },
    onError: (err) => {
      const fresh = err instanceof ApiError && err.status === 409 ? (err.data?.['plan'] as EventImportPlan | undefined) : undefined;
      if (fresh) { setNewer(fresh); setAcks({ underway: false, remove: false, dragFill: false }); }
    },
  });
  const staleNotice = doImport.error instanceof ApiError && doImport.error.status === 409;

  const at = (iso: string) => fmtInTz(iso, facilityTimezone);
  const fmtVal = (field: ImportField, v: string | null) => (v === null ? '(blank)' : field === 'start' ? at(v) : `"${v}"`);
  const applyCount = plan ? plan.new.length + plan.changed.length + plan.removed.length : 0;
  const blocked = !plan || plan.noValidRows || applyCount === 0
    || (plan.needs.underwayAck && !acks.underway) || (plan.needs.removeAck && !acks.remove) || (plan.needs.dragFillAck && !acks.dragFill);
  const applyLabel = !plan || applyCount === 0
    ? 'No changes — the sheet matches CSM'
    : 'Apply: ' + [
        plan.new.length && `${plan.new.length} new`,
        plan.changed.length && `${plan.changed.length} changed`,
        plan.removed.length && `${plan.removed.length} removed`,
      ].filter(Boolean).join(', ');

  const keptText = (k: EventImportPlan['kept'][number]) => {
    const says = k.sheetRow ? ` — the sheet now says ${teamsOf(k.sheetRow)} · Sheet ${k.sheetRow.sheetId} · ${at(k.sheetRow.start)}` : '';
    switch (k.reason) {
      case 'edited': return k.inSheet ? `edited in CSM, so not overwritten${says}` : 'edited in CSM — no longer in the sheet, kept';
      case 'deleted': return 'you removed this in CSM; the sheet still lists it, so it stays removed';
      case 'starting': return 'starting now — not changed by this import';
      case 'played': return `already ${k.status}${says}`;
    }
  };

  return (
    <ModalShell title="Import matches — review" onClose={onClose}>
      <div className="max-h-[60vh] overflow-y-auto px-4 py-3">
        {preview.isLoading ? (
          <div className="flex items-center justify-center py-8 text-gray-500"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Reading sheet…</div>
        ) : preview.isError || !plan ? (
          <p className="text-sm text-red-600 dark:text-red-400">Could not read the sheet: {(preview.error as Error)?.message ?? 'unknown error'}</p>
        ) : (
          <>
            {staleNotice && (
              <p className="mb-2 rounded border border-blue-300 bg-blue-50 px-2 py-1.5 text-xs text-blue-800 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-200">
                The sheet changed since you opened this review. Nothing was applied — this is the updated preview.
              </p>
            )}
            <p className="mb-3 text-sm text-gray-700 dark:text-gray-300">
              {[
                `${plan.new.length} new`, `${plan.changed.length} changed`, `${plan.unchanged.length} unchanged`, `${plan.removed.length} removed`,
                plan.kept.length ? `${plan.kept.length} kept` : null,
                plan.problems.length ? `${plan.problems.length} problem${plan.problems.length === 1 ? '' : 's'}` : null,
                plan.dragFill.length ? `${plan.dragFill.length} name${plan.dragFill.length === 1 ? '' : 's'} to check` : null,
              ].filter(Boolean).join(' · ')}
            </p>

            {plan.noValidRows && (
              <p className="mb-3 rounded border border-red-300 bg-red-50 px-2 py-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">
                <AlertTriangle className="mb-0.5 mr-1 inline h-4 w-4" />The sheet has no valid games. Nothing will change.
              </p>
            )}
            {!plan.noValidRows && plan.needs.dragFillAck && (
              <AckTick tone="amber" checked={acks.dragFill} onChange={v => setAcks(a => ({ ...a, dragFill: v }))}>
                <strong>Check these event names.</strong> {plan.dragFill.map(d => `Row ${d.rowNumber} says "${d.eventName}"`).join(', ')}, but most rows say
                "{plan.dragFill[0]!.majority}". Dragged down in Google Sheets? (It counts numbers up — hold Ctrl, or Cmd on a Mac, while dragging to copy instead.)
                Fix them in the sheet, or tick to import them as written.
              </AckTick>
            )}
            {!plan.noValidRows && plan.needs.removeAck && (
              <AckTick tone="red" checked={acks.remove} onChange={v => setAcks(a => ({ ...a, remove: v }))}>
                <strong>Yes, remove {plan.removed.length} game{plan.removed.length === 1 ? '' : 's'}</strong> that {plan.removed.length === 1 ? 'is' : 'are'} no longer in the sheet
                {plan.underway ? ' (the event is underway)' : ' (more than half of the upcoming games)'}. Removed games can be restored by adding them back to the sheet and importing again.
              </AckTick>
            )}
            {!plan.noValidRows && plan.needs.underwayAck && (
              <AckTick tone="amber" checked={acks.underway} onChange={v => setAcks(a => ({ ...a, underway: v }))}>
                <strong>This event is already underway.</strong> Tick to confirm you want to change not-yet-started games now.
              </AckTick>
            )}

            <ImportSection title="New" count={plan.new.length} tone="text-green-700 dark:text-green-400" open>
              {plan.new.map(n => (
                <ImportLine key={`n${n.row.rowNumber}`} when={at(n.row.start)} sheet={n.row.sheetId}>
                  {teamsOf(n.row)} <span className="text-gray-400">{n.row.eventName}</span>
                  {n.restoreId && <span className="ml-1 text-xs text-gray-500">(restores a game an earlier import removed)</span>}
                </ImportLine>
              ))}
            </ImportSection>
            <ImportSection title="Changed" count={plan.changed.length} tone="text-blue-700 dark:text-blue-400" open>
              {plan.changed.map(c => (
                <ImportLine key={`c${c.id}`} when={at(c.row.start)} sheet={c.row.sheetId}>
                  <span className="text-gray-500 dark:text-gray-400">{teamsOf(c.row)}{c.moved ? ' · moved' : ''}</span>
                  {c.diffs.map(d => (
                    <div key={d.field} className="text-xs">
                      {FIELD_LABEL[d.field]}: <span className="text-gray-500 line-through dark:text-gray-400">{fmtVal(d.field, d.from)}</span> → <strong>{fmtVal(d.field, d.to)}</strong>
                    </div>
                  ))}
                </ImportLine>
              ))}
            </ImportSection>
            <ImportSection title="Will be removed" count={plan.removed.length} tone="text-red-700 dark:text-red-400" open>
              {plan.removed.map(r => (
                <ImportLine key={`r${r.id}`} when={at(r.start)} sheet={r.sheetId}>{r.label} <span className="text-xs text-gray-500">— no longer in the sheet</span></ImportLine>
              ))}
            </ImportSection>
            <ImportSection title="Kept as is" count={plan.kept.length} tone="text-gray-700 dark:text-gray-300" open>
              {plan.kept.map(k => (
                <ImportLine key={`k${k.id}`} when={at(k.start)} sheet={k.sheetId}>{k.label} <span className="text-xs text-gray-500 dark:text-gray-400">— {keptText(k)}</span></ImportLine>
              ))}
            </ImportSection>
            <ImportSection title="Problems (not imported)" count={plan.problems.length} tone="text-red-700 dark:text-red-400" open>
              {plan.problems.map(p => (
                <div key={`p${p.rowNumber}`} className="py-1.5 text-xs text-red-700 dark:text-red-300">Row {p.rowNumber}: {p.reason}</div>
              ))}
            </ImportSection>
            <ImportSection title="Unchanged" count={plan.unchanged.length} tone="text-gray-500 dark:text-gray-400">
              {plan.unchanged.map(u => (
                <ImportLine key={`u${u.id}`} when={at(u.row.start)} sheet={u.row.sheetId}>{teamsOf(u.row)}</ImportLine>
              ))}
            </ImportSection>
          </>
        )}
      </div>
      <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-4 py-3 dark:border-gray-700">
        {doImport.isError && !staleNotice && (
          <span className="mr-auto text-xs text-red-600 dark:text-red-400">{(doImport.error as Error).message}</span>
        )}
        <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
        {plan && !plan.noValidRows && (
          <Button size="sm" onClick={() => doImport.mutate()} disabled={blocked || doImport.isPending}>
            {doImport.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Upload className="mr-1 h-4 w-4" />}
            {applyLabel}
          </Button>
        )}
      </div>
    </ModalShell>
  );
}

// ── Active event: matches grouped by draw + controls ─────────────────────────
function ActiveEvent({ event, facilityId, facilityTimezone, sheetsEnabled, broadcastModeActive, curlOps }: { event: EventSummary; facilityId: string; facilityTimezone?: string; sheetsEnabled: boolean; broadcastModeActive?: boolean; curlOps?: boolean }) {
  const qc = useQueryClient();
  // Games of a CurlOps-linked event come from CurlOps and are started/stopped there.
  const linked = event.curlOpsEventId != null;
  const [showCurlOps, setShowCurlOps] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editMatch, setEditMatch] = useState<StreamSchedule | null>(null);
  const [busy, setBusy] = useState<string | null>(null); // sheetId currently issuing a command

  const detail = useQuery({
    queryKey: queryKeys.events.detail(event.id),
    queryFn: () => eventsApi.get(event.id),
    refetchInterval: 15_000,
  });
  const matches = detail.data?.event.matches ?? [];
  // Draft = prepared in advance; league still auto-starting. Draws are NOT startable
  // until the event is activated (which pauses the league).
  const isDraft = event.status === 'draft';

  // Poll command progress fast while anything is in-flight.
  const commandsQuery = useQuery({
    queryKey: queryKeys.schedules.commands(facilityId),
    queryFn: () => schedulesApi.getAgentCommands(facilityId),
    refetchInterval: (q) => {
      const cmds = (q.state.data?.commands ?? []) as AgentCommandStatus[];
      const active = cmds.some(c => ['pending', 'delivered', 'running'].includes(c.status));
      return active ? 3_000 : 15_000;
    },
  });
  // As soon as any in-flight command finishes, refetch the matches so the row
  // shows its new state now rather than at the next 15 s poll.
  const activeIds = useRef<Set<number>>(new Set());
  useEffect(() => {
    const cmds = (commandsQuery.data?.commands ?? []) as AgentCommandStatus[];
    const now = new Set(cmds.filter(c => ['pending', 'delivered', 'running'].includes(c.status)).map(c => c.id));
    const finished = [...activeIds.current].some(id => !now.has(id));
    activeIds.current = now;
    if (finished) void detail.refetch();
  }, [commandsQuery.data]); // eslint-disable-line react-hooks/exhaustive-deps

  // Start commands are tied to a specific match (scheduleId) — a sheet appears in
  // multiple draws, so keying by sheet alone would cross-show a command on the
  // wrong draw's same-sheet row. Stop commands have no schedule row → key by sheet.
  const { startCmdById, stopCmdBySheet } = useMemo(() => {
    const startCmdById = new Map<number, AgentCommandStatus>();
    const stopCmdBySheet = new Map<string, AgentCommandStatus>();
    for (const c of commandsQuery.data?.commands ?? []) {
      if (c.type === 'start' && c.scheduleId != null) {
        if (!startCmdById.has(c.scheduleId)) startCmdById.set(c.scheduleId, c);
      } else if (c.type === 'stop') {
        if (!stopCmdBySheet.has(c.sheetName)) stopCmdBySheet.set(c.sheetName, c);
      }
    }
    return { startCmdById, stopCmdBySheet };
  }, [commandsQuery.data]);
  const cmdForMatch = (m: StreamSchedule): AgentCommandStatus | undefined =>
    m.status === 'streaming' ? stopCmdBySheet.get(m.sheetId) : startCmdById.get(m.id);

  // Host-online + per-sheet state (for the pre-flight modal).
  const agentStatusQuery = useQuery({
    queryKey: queryKeys.schedules.agentStatus(facilityId),
    queryFn: () => schedulesApi.getAgentStatus(facilityId),
    refetchInterval: 30_000,
  });
  const stateBySheet = useMemo(() => {
    const m = new Map<string, { online: boolean; state: string }>();
    for (const h of agentStatusQuery.data?.hosts ?? []) {
      for (const s of h.sessions ?? []) {
        const letter = s.sheetName.replace(/^Sheet\s+/i, '').trim();
        m.set(letter, { online: h.isOnline, state: s.state });
      }
    }
    return m;
  }, [agentStatusQuery.data]);

  const [preflightDraw, setPreflightDraw] = useState<string | null>(null);
  const [showImport, setShowImport] = useState(false);
  const [expandedMatches, setExpandedMatches] = useState<Set<number>>(new Set());
  const [expandedDraws, setExpandedDraws] = useState<Set<string>>(new Set());
  const toggleMatch = (id: number) => setExpandedMatches(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleDraw = (k: string) => setExpandedDraws(s => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: queryKeys.events.detail(event.id) });
    qc.invalidateQueries({ queryKey: queryKeys.schedules.commands(facilityId) });
    qc.invalidateQueries({ queryKey: queryKeys.schedules.all });
  };

  // For a CurlOps-linked event each start/stop asks for the override first.
  const withOverride = (action: string) => {
    const extra = curlOpsOverride(event, isAdmin, action);
    if (extra === null) throw new Error(CANCELLED);
    return extra;
  };
  const startSheet = useMutation({
    mutationFn: async (m: StreamSchedule) => schedulesApi.sendAgentCommand({ facilityId, type: 'start', scheduleId: m.id, ...withOverride('Start this game') }),
    onError: lockAlert,
    onSettled: () => { setBusy(null); invalidate(); },
  });
  const stopSheet = useMutation({
    mutationFn: async (m: StreamSchedule) => schedulesApi.sendAgentCommand({ facilityId, type: 'stop', sheetName: m.sheetId, ...withOverride('Stop this game') }),
    onError: lockAlert,
    onSettled: () => { setBusy(null); invalidate(); },
  });
  const startDraw = useMutation({
    mutationFn: async (v: { scheduledStart: string; scheduleIds: number[] }) =>
      schedulesApi.startDraw({ facilityId, scheduledStart: v.scheduledStart, eventId: event.id, scheduleIds: v.scheduleIds, ...withOverride('Start this draw') }),
    onError: lockAlert,
    onSettled: () => { setBusy(null); invalidate(); },
  });
  const curlOpsSync = useMutation({
    mutationFn: () => eventsApi.curlOpsSync(event.id),
    onSuccess: (r) => {
      invalidate();
      qc.invalidateQueries({ queryKey: queryKeys.events.list(facilityId) });
      const s = r.sync;
      const summary = `${s.created} added, ${s.updated} changed, ${s.removed} removed, ${s.unchanged} unchanged.`;
      if (s.problems.length) alert(`Synced from CurlOps: ${summary}\n\n${s.problems.join('\n')}`);
    },
    onError: (e: unknown) => alert(e instanceof Error ? e.message : 'Sync from CurlOps failed'),
  });
  const deleteMatch = useMutation({
    mutationFn: (id: number) => schedulesApi.delete(id),
    onSettled: () => invalidate(),
  });
  const deleteBatch = useMutation({
    mutationFn: async (ms: StreamSchedule[]) => { await Promise.all(ms.map(m => schedulesApi.delete(m.id))); },
    onSettled: () => invalidate(),
  });

  const extend = useMutation({
    mutationFn: (iso: string) => eventsApi.update(event.id, { eventModeUntil: iso }),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.events.list(facilityId) }),
  });
  const endEvent = useMutation({
    mutationFn: () => eventsApi.end(event.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.events.all }),
  });
  const activate = useMutation({
    mutationFn: (iso: string) => eventsApi.activate(event.id, iso),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.events.all }),
    onError: (e: unknown) => alert(e instanceof Error ? e.message : 'Could not start the event'),
  });
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const deleteEvent = useMutation({
    mutationFn: () => eventsApi.delete(event.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.events.all }),
  });

  const makeSheet = useMutation({
    mutationFn: () => eventsApi.createSheet({ facilityId, eventId: event.id, name: event.name }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: queryKeys.events.detail(event.id) }); qc.invalidateQueries({ queryKey: queryKeys.events.list(facilityId) }); },
  });

  // Group matches by draw (scheduledStart).
  const draws = useMemo(() => {
    const groups = new Map<string, StreamSchedule[]>();
    for (const m of matches) {
      const key = m.scheduledStart;
      (groups.get(key) ?? groups.set(key, []).get(key)!).push(m);
    }
    return [...groups.entries()].sort((a, b) => new Date(a[0]).getTime() - new Date(b[0]).getTime());
  }, [matches]);

  // Advisory: estimated game length → flag same-sheet games that overlap or start
  // in a tight window. NO scheduling action — just a heads-up for manual start/stop.
  const estMinutes = (event.format && event.numEnds) ? (GAME_MINUTES[event.format]?.[event.numEnds] ?? null) : null;
  const flaggedById = useMemo(() => {
    const flags = new Map<number, { kind: 'overlap' | 'tight'; gapMin: number }>();
    if (!estMinutes) return flags;
    const bySheet = new Map<string, StreamSchedule[]>();
    for (const m of matches) { (bySheet.get(m.sheetId) ?? bySheet.set(m.sheetId, []).get(m.sheetId)!).push(m); }
    for (const list of bySheet.values()) {
      const sorted = [...list].sort((a, b) => new Date(a.scheduledStart).getTime() - new Date(b.scheduledStart).getTime());
      for (let i = 1; i < sorted.length; i++) {
        const prev = sorted[i - 1]!;
        const cur = sorted[i]!;
        const prevEnd = new Date(prev.scheduledStart).getTime() + estMinutes * 60_000;
        const gapMin = Math.round((new Date(cur.scheduledStart).getTime() - prevEnd) / 60_000);
        if (gapMin < 0) flags.set(cur.id, { kind: 'overlap', gapMin });
        else if (gapMin < TIGHT_BUFFER_MIN) flags.set(cur.id, { kind: 'tight', gapMin });
      }
    }
    return flags;
  }, [matches, estMinutes]);

  // When the last game still to play should finish: its start + the format
  // estimate (or its own stored end). If Event Mode ends before then, the agent
  // goes back to the league schedule and stops any event stream still live.
  const lastGameEnd = useMemo(() => {
    let last = 0;
    for (const m of matches) {
      if (m.status !== 'pending' && m.status !== 'streaming') continue;
      if (m.status === 'pending' && m.isPaused) continue;
      const start = new Date(m.scheduledStart).getTime();
      last = Math.max(last, estMinutes ? start + estMinutes * 60_000 : new Date(m.scheduledEnd).getTime());
    }
    return last || null;
  }, [matches, estMinutes]);
  const endsTooSoon = (untilMs: number) => lastGameEnd !== null && untilMs < lastGameEnd;
  const lapseRisk = !isDraft && !!event.eventModeUntil && endsTooSoon(new Date(event.eventModeUntil).getTime());
  // A suggested end that covers the last game, with half an hour to spare.
  // The time the club entered always wins; confirmEndsTooSoon warns if it's before
  // the last game. Only with no time on file do we suggest last game + 30 min.
  const suggestedUntil = (current: string | null | undefined): string | null | undefined =>
    current || (lastGameEnd !== null ? new Date(lastGameEnd + 30 * 60_000).toISOString() : current);
  // Extending has no form behind it, so propose a time that covers the last game.
  const suggestedExtension = (current: string | null | undefined): string | null | undefined =>
    lastGameEnd !== null && (!current || endsTooSoon(new Date(current).getTime()))
      ? new Date(lastGameEnd + 30 * 60_000).toISOString()
      : current;
  const confirmEndsTooSoon = (d: Date) => !endsTooSoon(d.getTime()) || confirm(
    `Event Mode would end at ${fmtInTz(d.toISOString(), facilityTimezone)}, before the last game is expected to finish ` +
    `(~${fmtInTz(new Date(lastGameEnd!).toISOString(), facilityTimezone)}).\n\n` +
    `When Event Mode ends, the rink goes back to the league schedule and any event stream still live is stopped. Use this time anyway?`);

  const isDrawComplete = (ms: StreamSchedule[]) => ms.length > 0 && ms.every(m => m.status === 'completed' || m.status === 'cancelled');
  const activeDraws = draws.filter(([, ms]) => !isDrawComplete(ms));
  const completeDraws = draws.filter(([, ms]) => isDrawComplete(ms));

  // One game's box: header (sheet/teams/status/actions) + an expandable checklist.
  const renderMatch = (m: StreamSchedule) => {
    const cmd = cmdForMatch(m);                 // current action (start while pending, stop while streaming)
    const startCmd = startCmdById.get(m.id);    // the start attempt — drives the retained checklist
    const inflight = cmd && ['pending', 'delivered', 'running'].includes(cmd.status);
    const isStreaming = m.status === 'streaming';
    const isPending = m.status === 'pending';
    // Start finished but the match row hasn't caught up to 'streaming' yet (shows Live) — don't offer a second start.
    const justStarted = isPending && cmd?.type === 'start' && cmd.status === 'done';
    const flagged = flaggedById.get(m.id);
    const expanded = expandedMatches.has(m.id);
    const hasSteps = (startCmd?.stageHistory?.length ?? 0) > 0 || !!m.startupLog || !!m.streamEndedAt || startCmd?.status === 'failed';
    return (
      <div key={m.id} className="rounded border border-gray-200 dark:border-gray-700">
        <div className="flex flex-wrap items-center gap-2 px-2 py-2">
          <span className="rounded bg-blue-100 px-1.5 py-0.5 text-xs font-semibold text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">Sheet {m.sheetId}</span>
          <span className="min-w-0 flex-1 truncate text-sm text-gray-700 dark:text-gray-300">
            {m.team1Name && m.team2Name ? `${m.team1Name} vs ${m.team2Name}` : (m.team1Name || m.team2Name || m.eventName)}
          </span>
          <MatchProgress match={m} cmd={cmd} />
          {flagged && (
            <span
              title={flagged.kind === 'overlap'
                ? `Estimated overlap with the previous game on Sheet ${m.sheetId} by ~${Math.abs(flagged.gapMin)} min`
                : `Only ~${flagged.gapMin} min after the previous game's estimated end on Sheet ${m.sheetId}`}
              className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-xs font-medium ${flagged.kind === 'overlap' ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'}`}
            >
              <AlertTriangle className="h-3 w-3" /> {flagged.kind === 'overlap' ? 'Overlap' : 'Tight'}
            </span>
          )}
          {m.manuallyEdited && (
            <span title="Edited in CSM — protected from being overwritten on re-import" className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-1.5 py-0.5 text-xs font-medium text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
              <Pencil className="h-3 w-3" /> Edited
            </span>
          )}
          {m.youtubeBroadcastId && (isStreaming || m.status === 'completed') && (
            <a href={`https://www.youtube.com/watch?v=${m.youtubeBroadcastId}`} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline dark:text-blue-400">
              <ExternalLink className="h-3 w-3" /> Watch
            </a>
          )}
          <div className="flex items-center gap-1">
            {isStreaming ? (
              <Button variant="outline" size="sm" onClick={() => { setBusy(m.sheetId); stopSheet.mutate(m); }} disabled={!!inflight || broadcastModeActive || (stopSheet.isPending && busy === m.sheetId)} title={broadcastModeActive ? 'Disabled — Broadcast Mode is on' : linked ? 'Controlled by CurlOps (Finished) — needs the override code here' : undefined}>
                {linked ? <Lock className="mr-1 h-3.5 w-3.5" /> : <Square className="mr-1 h-3.5 w-3.5" />} Stop
              </Button>
            ) : isPending ? (
              <Button size="sm" variant={linked ? 'outline' : 'default'} onClick={() => { setBusy(m.sheetId); startSheet.mutate(m); }} disabled={isDraft || !!inflight || justStarted || broadcastModeActive || (startSheet.isPending && busy === m.sheetId)} title={broadcastModeActive ? 'Disabled — Broadcast Mode is on' : isDraft ? 'Start the event to run draws' : justStarted ? 'Going live — refreshing…' : linked ? 'Controlled by CurlOps (Start game) — needs the override code here' : undefined}>
                {inflight ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : linked ? <Lock className="mr-1 h-3.5 w-3.5" /> : <Play className="mr-1 h-3.5 w-3.5" />} Start
              </Button>
            ) : null}
            {isPending && (!linked || isAdmin) && (
              <button title="Edit match" onClick={() => setEditMatch(m)} className="rounded p-1 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20"><Pencil className="h-4 w-4" /></button>
            )}
            {((isPending && !linked) || (isAdmin && !isStreaming)) && (
              <button title="Delete match" onClick={() => { if (confirm('Remove this match from the event?')) deleteMatch.mutate(m.id); }} className="rounded p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"><Trash2 className="h-4 w-4" /></button>
            )}
            {hasSteps && (
              <button onClick={() => toggleMatch(m.id)} className="rounded px-1.5 py-1 text-xs text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800">
                {expanded ? '▾' : '▸'} steps
              </button>
            )}
          </div>
        </div>
        {expanded && hasSteps && (
          <div className="border-t border-gray-100 dark:border-gray-800">
            <StreamChecklist startCmd={startCmd} match={m} {...(facilityTimezone ? { tz: facilityTimezone } : {})} />
          </div>
        )}
      </div>
    );
  };

  const onExtend = () => {
    const input = window.prompt(
      `Extend Event Mode until (YYYY-MM-DD HH:MM, ${facilityTimezone ?? 'facility local'}):`,
      toZonedInputValue(suggestedExtension(event.eventModeUntil), facilityTimezone).replace('T', ' '),
    );
    if (!input) return;
    // Read as rink time — the prompt has always said "facility local" but used to
    // parse in the browser's zone, quietly shifting the end by the offset.
    const d = zonedInputToInstant(input, facilityTimezone);
    if (!d || d.getTime() <= Date.now()) { alert('Enter a valid future date/time.'); return; }
    if (!confirmEndsTooSoon(d)) return;
    extend.mutate(d.toISOString());
  };

  const onActivate = () => {
    // The old default here was the stored instant sliced as UTC, presented under a
    // "facility local" label — wrong by the facility's offset before you typed.
    const input = window.prompt(
      `Start Event Mode now — auto-end at (YYYY-MM-DD HH:MM, ${facilityTimezone ?? 'facility local'}):`,
      toZonedInputValue(suggestedUntil(event.eventModeUntil), facilityTimezone).replace('T', ' '));
    if (!input) return;
    const d = zonedInputToInstant(input, facilityTimezone);
    if (!d || d.getTime() <= Date.now()) { alert('Enter a valid future date/time.'); return; }
    if (!confirmEndsTooSoon(d)) return;
    if (!confirm('Start Event Mode now? This pauses the league auto-start for this facility until the end time.')) return;
    activate.mutate(d.toISOString());
  };

  return (
    <div className="space-y-4">
      {/* Banner — draft (league running) vs active Event Mode */}
      <div className={`rounded-lg border p-4 ${isDraft ? 'border-blue-300 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20' : 'border-purple-300 bg-purple-50 dark:border-purple-800 dark:bg-purple-900/20'}`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className={`flex items-center gap-2 ${isDraft ? 'text-blue-800 dark:text-blue-200' : 'text-purple-800 dark:text-purple-200'}`}>
              {isDraft ? <CalendarPlus className="h-5 w-5" /> : <RadioTower className="h-5 w-5" />}
              <span className="text-lg font-semibold">{isDraft ? `Draft: ${event.name}` : `Event Mode: ${event.name}`}</span>
            </div>
            <p className={`mt-1 text-sm ${isDraft ? 'text-blue-700 dark:text-blue-300' : 'text-purple-700 dark:text-purple-300'}`}>
              {isDraft ? (
                <>League keeps auto-starting — prepare matches below, then <strong>Start event</strong> to pause the league and run the draws.</>
              ) : (
                <>League schedule is suspended. <Clock className="mb-0.5 inline h-3.5 w-3.5" /> Auto-ends in <strong>{countdown(event.eventModeUntil!)}</strong> ({fmtInTz(event.eventModeUntil!, facilityTimezone)}).</>
              )}
              {event.format && event.numEnds && (
                <span className={`ml-1 ${isDraft ? 'text-blue-500 dark:text-blue-400' : 'text-purple-500 dark:text-purple-400'}`}>· {FORMAT_LABEL[event.format]}, {event.numEnds} ends{estMinutes ? ` (~${estMinutes} min/game)` : ''}</span>
              )}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowEdit(v => !v)}>
              <Pencil className="mr-1 h-4 w-4" /> Edit details
            </Button>
            {linked && (
              <Button variant="outline" size="sm" onClick={() => curlOpsSync.mutate()} disabled={curlOpsSync.isPending} title="Pull this event's games from CurlOps now (it also happens every 5 minutes)">
                {curlOpsSync.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1 h-4 w-4" />} Sync now
              </Button>
            )}
            {(curlOps || linked) && (
              <Button variant="outline" size="sm" onClick={() => setShowCurlOps(v => !v)}>
                <Link2 className="mr-1 h-4 w-4" /> {linked ? 'CurlOps link' : 'Link to CurlOps'}
              </Button>
            )}
            {!linked && !event.googleSheetUrl && sheetsEnabled && (
              <Button variant="outline" size="sm" onClick={() => makeSheet.mutate()} disabled={makeSheet.isPending}>
                {makeSheet.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-1 h-4 w-4" />}
                Create Google Sheet
              </Button>
            )}
            {!linked && event.googleSheetUrl && (
              <a href={event.googleSheetUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded border border-gray-300 px-2 py-1.5 text-xs text-blue-600 hover:bg-gray-50 dark:border-gray-600 dark:text-blue-400 dark:hover:bg-gray-800">
                <ExternalLink className="h-3.5 w-3.5" /> Open sheet
              </a>
            )}
            {!linked && event.googleSheetUrl && (
              <Button variant="outline" size="sm" onClick={() => setShowImport(true)}>
                <Upload className="mr-1 h-4 w-4" /> Import matches
              </Button>
            )}
            {isDraft ? (
              <>
                <Button size="sm" onClick={onActivate} disabled={activate.isPending}>
                  {activate.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <RadioTower className="mr-1 h-4 w-4" />} Start event
                </Button>
                <Button variant="outline" size="sm" onClick={() => { if (confirm(`Discard draft "${event.name}"?`)) endEvent.mutate(); }} disabled={endEvent.isPending}>
                  <XCircle className="mr-1 h-4 w-4" /> Discard
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" size="sm" onClick={onExtend} disabled={extend.isPending}>Extend</Button>
                <Button variant="destructive" size="sm" onClick={() => { if (confirm('End Event Mode now? The league schedule will resume.')) endEvent.mutate(); }} disabled={endEvent.isPending}>
                  <XCircle className="mr-1 h-4 w-4" /> End Event
                </Button>
              </>
            )}
            {isAdmin && (
              <Button variant="outline" size="sm" onClick={() => { if (confirm(`Permanently DELETE "${event.name}" and all its games? This cannot be undone.`)) deleteEvent.mutate(); }} disabled={deleteEvent.isPending}>
                <Trash2 className="mr-1 h-4 w-4" /> Delete Event
              </Button>
            )}
          </div>
        </div>
      </div>

      {showEdit && <EditEventDetails event={event} facilityId={facilityId} onDone={() => setShowEdit(false)} />}
      {showCurlOps && <CurlOpsSettings event={event} onDone={() => setShowCurlOps(false)} />}

      {linked && (
        <div className="flex items-start gap-2 rounded-md border border-teal-300 bg-teal-50 px-3 py-2 text-sm text-teal-900 dark:border-teal-800 dark:bg-teal-900/20 dark:text-teal-100">
          <Lock className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            <strong>Run from CurlOps</strong> (event {event.curlOpsEventId}). Games come from CurlOps
            {event.lastCurlOpsSync ? <> — last synced {fmtInTz(event.lastCurlOpsSync, facilityTimezone)}</> : null}.
            Each stream starts when <strong>Start game</strong> is pressed in CurlOps and stops at <strong>Finished</strong>;
            nothing stops on its own. Start/Stop here need the override code{event.hasOverrideCode ? '' : ' (none set — admins only)'}.
            {isDraft && <> <strong>CurlOps can only start games while Event Mode is on</strong> — Start event before the first draw.</>}
          </span>
        </div>
      )}

      {lapseRisk && (
        <div className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span className="flex-1">
            <strong>Event Mode ends before the last game.</strong> It ends {fmtInTz(event.eventModeUntil!, facilityTimezone)}, but the
            last game is expected to finish around {fmtInTz(new Date(lastGameEnd!).toISOString(), facilityTimezone)}. When Event Mode ends,
            the rink goes back to the league schedule and <strong>any event stream still live is stopped</strong>.
          </span>
          <Button size="sm" variant="outline" onClick={onExtend} disabled={extend.isPending}>Extend</Button>
        </div>
      )}

      {/* Manual-stop + overlap advisory (active only) / draft note */}
      {isDraft ? (
        <div className="rounded-md border border-blue-300 bg-blue-50 px-3 py-2 text-sm text-blue-800 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-200">
          <CalendarPlus className="mb-0.5 mr-1 inline h-4 w-4" />
          This is a <strong>draft</strong> — import and edit matches now. Draws can be started once you <strong>Start event</strong>.
          {flaggedById.size > 0 && (
            <span className="ml-1">{flaggedById.size} game{flaggedById.size === 1 ? '' : 's'} may overlap or start in a tight window on the same sheet (based on the ~{estMinutes} min estimate).</span>
          )}
        </div>
      ) : linked ? null : (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
          <AlertTriangle className="mb-0.5 mr-1 inline h-4 w-4" />
          <strong>Event streams don't start or stop on their own.</strong> Start each draw here, and <strong>Stop</strong> each stream when its game ends.
          {flaggedById.size > 0 && (
            <span className="ml-1">{flaggedById.size} game{flaggedById.size === 1 ? '' : 's'} may overlap or start in a tight window on the same sheet (based on the ~{estMinutes} min estimate) — starting/stopping those may need extra attention.</span>
          )}
        </div>
      )}

      {/* Draws */}
      {detail.isLoading ? (
        <div className="flex items-center justify-center py-10 text-gray-500"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading matches…</div>
      ) : draws.length === 0 ? (
        <Card><CardContent className="py-8 text-center text-gray-500 dark:text-gray-400">
          No matches loaded yet. {linked
            ? 'No games from CurlOps yet — check the CurlOps event ID, then use Sync now.'
            : event.googleSheetUrl ? 'Use "Import matches" to pull them from the shared sheet.' : 'Add a Google Sheet URL to this event, then import matches.'}
        </CardContent></Card>
      ) : (
        <>
          {activeDraws.map(([drawStart, drawMatches]) => {
            const label = drawMatches.find(m => m.drawLabel)?.drawLabel;
            const anyStartable = drawMatches.some(m => m.status === 'pending');
            return (
              <Card key={drawStart}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 py-3">
                  <CardTitle className="text-base">
                    {label ? `${label} — ` : 'Draw Batch — '}{fmtInTz(drawStart, facilityTimezone)}
                    <span className="ml-2 text-xs font-normal text-gray-400">{drawMatches.length} game{drawMatches.length === 1 ? '' : 's'}</span>
                  </CardTitle>
                  {/* CurlOps starts each game itself; a single game can still be
                      started here with the override code, but never a whole batch. */}
                  {!linked && (
                    <Button
                      size="sm"
                      onClick={() => setPreflightDraw(drawStart)}
                      disabled={!anyStartable || isDraft || broadcastModeActive}
                      title={broadcastModeActive ? 'Disabled — Broadcast Mode is on' : isDraft ? 'Start the event to run draws' : 'Pre-flight and start every not-yet-started sheet in this batch (staggered)'}
                    >
                      <Play className="mr-1 h-4 w-4" />
                      Start Draw Batch
                    </Button>
                  )}
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-1.5">
                    {drawMatches.slice().sort((a, b) => a.sheetId.localeCompare(b.sheetId)).map(renderMatch)}
                  </div>
                </CardContent>
              </Card>
            );
          })}

          {completeDraws.length > 0 && (
            <div className="pt-2">
              <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">Completed</h3>
              <div className="space-y-2">
                {completeDraws.map(([drawStart, drawMatches]) => {
                  const label = drawMatches.find(m => m.drawLabel)?.drawLabel;
                  const open = expandedDraws.has(drawStart);
                  return (
                    <Card key={drawStart} className="opacity-90">
                      <div className="flex items-center gap-2 pr-3">
                        <button onClick={() => toggleDraw(drawStart)} className="flex flex-1 items-center gap-2 px-4 py-2.5 text-left text-sm text-gray-600 dark:text-gray-300">
                          <span className="text-gray-400">{open ? '▾' : '▸'}</span>
                          <span>{label ? `${label} — ` : 'Draw Batch — '}{fmtInTz(drawStart, facilityTimezone)}</span>
                          <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-400"><CheckCircle2 className="h-3 w-3" /> Complete</span>
                          <span className="text-xs text-gray-400">{drawMatches.length} game{drawMatches.length === 1 ? '' : 's'}</span>
                        </button>
                        {isAdmin && (
                          <Button
                            variant="outline" size="sm"
                            onClick={() => { if (confirm(`Delete all ${drawMatches.length} games in this completed batch? They'll be removed from the event (a re-import will re-add them).`)) deleteBatch.mutate(drawMatches); }}
                            disabled={deleteBatch.isPending}
                            title="Remove this completed batch's games from the event"
                          >
                            <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete batch
                          </Button>
                        )}
                      </div>
                      {open && (
                        <CardContent className="pt-0">
                          <div className="space-y-1.5">
                            {drawMatches.slice().sort((a, b) => a.sheetId.localeCompare(b.sheetId)).map(renderMatch)}
                          </div>
                        </CardContent>
                      )}
                    </Card>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {editMatch && (
        <ScheduleFormModal
          open={!!editMatch}
          onClose={() => { setEditMatch(null); invalidate(); }}
          facilityId={facilityId}
          {...(facilityTimezone ? { facilityTimezone } : {})}
          schedule={editMatch}
        />
      )}

      {preflightDraw && (
        <PreflightModal
          drawStart={preflightDraw}
          batch={draws.find(([s]) => s === preflightDraw)?.[1] ?? []}
          {...(facilityTimezone ? { facilityTimezone } : {})}
          stateBySheet={stateBySheet}
          starting={startDraw.isPending}
          onClose={() => setPreflightDraw(null)}
          onConfirm={ids => { const d = preflightDraw; setBusy(d); startDraw.mutate({ scheduledStart: d, scheduleIds: ids }, { onSuccess: () => setPreflightDraw(null) }); }}
        />
      )}

      {showImport && (
        <ImportPreviewModal
          eventId={event.id}
          {...(facilityTimezone ? { facilityTimezone } : {})}
          onClose={() => setShowImport(false)}
          onImported={() => invalidate()}
        />
      )}
    </div>
  );
}

// ── Past (ended) events — admin can clear/remove them ────────────────────────
function PastEvents({ facilityId, events, facilityTimezone }: { facilityId: string; events: EventSummary[]; facilityTimezone?: string }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  // Ended, or active-but-expired (window lapsed without "End Event"). Drafts are
  // NOT past — they're upcoming and live in their own section.
  const ended = events.filter(e => e.status !== 'draft' && !e.isInEventMode);
  const del = useMutation({
    mutationFn: (id: number) => eventsApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.events.list(facilityId) }),
  });
  if (ended.length === 0) return null;
  return (
    <div className="pt-4">
      <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">Past events</h3>
      <div className="space-y-2">
        {ended.map(e => (
          <div key={e.id} className="flex items-center justify-between rounded border border-gray-200 px-3 py-2 dark:border-gray-700">
            <div className="text-sm">
              <span className="font-medium text-gray-800 dark:text-gray-200">{e.name}</span>
              <span className="ml-2 text-xs text-gray-400">{e.matchCount ?? 0} game{e.matchCount === 1 ? '' : 's'}{e.eventModeUntil ? ` · ended ${fmtInTz(e.eventModeUntil, facilityTimezone)}` : ''}</span>
            </div>
            {isAdmin && (
              <Button variant="outline" size="sm" onClick={() => { if (confirm(`Permanently delete "${e.name}" and all its games? This cannot be undone.`)) del.mutate(e.id); }} disabled={del.isPending}>
                <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete
              </Button>
            )}
          </div>
        ))}
      </div>
      {!isAdmin && <p className="mt-1 text-xs text-gray-400">Only an administrator can remove past events.</p>}
    </div>
  );
}

// ── Draft (upcoming) events — prepared without pausing the league ─────────────
function DraftEvents({ facilityId, drafts, facilityTimezone, sheetsEnabled, curlOps }: { facilityId: string; drafts: EventSummary[]; facilityTimezone?: string; sheetsEnabled: boolean; curlOps?: boolean }) {
  const [open, setOpen] = useState<Set<number>>(new Set());
  const toggle = (id: number) => setOpen(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  if (drafts.length === 0) return null;
  return (
    <div className="pt-2">
      <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">Upcoming events (drafts — league not paused)</h3>
      <div className="space-y-2">
        {drafts.map(d => {
          const isOpen = open.has(d.id);
          return (
            <div key={d.id} className="rounded border border-blue-200 dark:border-blue-900/50">
              <button onClick={() => toggle(d.id)} className="flex w-full items-center gap-2 px-3 py-2.5 text-left">
                <span className="text-gray-400">{isOpen ? '▾' : '▸'}</span>
                <CalendarPlus className="h-4 w-4 text-blue-500" />
                <span className="font-medium text-gray-800 dark:text-gray-200">{d.name}</span>
                <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-1.5 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">Draft</span>
                {d.curlOpsEventId != null && <span className="inline-flex items-center gap-1 rounded-full bg-teal-100 px-1.5 py-0.5 text-xs font-medium text-teal-700 dark:bg-teal-900/40 dark:text-teal-300"><Lock className="h-3 w-3" /> CurlOps</span>}
                <span className="text-xs text-gray-400">{d.matchCount ?? 0} game{d.matchCount === 1 ? '' : 's'}</span>
                <span className="ml-auto text-xs text-gray-400">{isOpen ? 'Hide' : 'Manage'}</span>
              </button>
              {isOpen && (
                <div className="border-t border-blue-100 px-3 py-3 dark:border-blue-900/40">
                  <ActiveEvent event={d} facilityId={facilityId} sheetsEnabled={sheetsEnabled} curlOps={!!curlOps} {...(facilityTimezone ? { facilityTimezone } : {})} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Panel ────────────────────────────────────────────────────────────────────
export default function EventModePanel({ facilityId, facilityTimezone, broadcastModeActive, curlOps }: { facilityId: string | null; facilityTimezone?: string; broadcastModeActive?: boolean; curlOps?: boolean }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.events.list(facilityId ?? ''),
    queryFn: () => eventsApi.list(facilityId ?? ''),
    enabled: !!facilityId,
    refetchInterval: 30_000,
  });
  const sheetsCfg = useQuery({
    queryKey: ['events', 'sheets-config'],
    queryFn: () => eventsApi.sheetsConfig(),
    staleTime: 5 * 60 * 1000,
  });
  const sheetsEnabled = !!sheetsCfg.data?.enabled;

  if (!facilityId) return <p className="text-sm text-gray-500 dark:text-gray-400">Select a facility to manage events.</p>;
  if (isLoading) return <div className="flex items-center justify-center py-10 text-gray-500"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…</div>;

  const events = data?.events ?? [];
  const active = events.find(e => e.isInEventMode);
  const drafts = events.filter(e => e.status === 'draft');

  return (
    <div className="space-y-4">
      {active
        ? <ActiveEvent event={active} facilityId={facilityId} sheetsEnabled={sheetsEnabled} broadcastModeActive={!!broadcastModeActive} curlOps={!!curlOps} {...(facilityTimezone ? { facilityTimezone } : {})} />
        : <CreateEventForm facilityId={facilityId} {...(facilityTimezone ? { facilityTimezone } : {})} sheetsEnabled={sheetsEnabled} curlOps={!!curlOps} onCreated={() => qc.invalidateQueries({ queryKey: queryKeys.events.list(facilityId) })} />}
      <DraftEvents facilityId={facilityId} drafts={drafts} sheetsEnabled={sheetsEnabled} curlOps={!!curlOps} {...(facilityTimezone ? { facilityTimezone } : {})} />
      <PastEvents facilityId={facilityId} events={events} {...(facilityTimezone ? { facilityTimezone } : {})} />
    </div>
  );
}
